import { useState, useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export type AddNvrFormValues = {
  name: string;
  brand: string;
  useHttps: boolean;
  host: string;
  port: string;
  username: string;
  password: string;
};

const DEFAULT_BRANDS = ["Milestone", "Axis", "Hikvision", "Dahua", "Other"] as const;

export type AddNvrDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddNvrFormValues) => void | Promise<void>;
};

const emptyForm = (): AddNvrFormValues => ({
  name: "",
  brand: "Milestone",
  useHttps: true,
  host: "",
  port: "80",
  username: "",
  password: "",
});

/**
 * Modal form to add an NVR (matches app NVR management flow).
 */
export function AddNvrDialog({ open, onClose, onSubmit }: AddNvrDialogProps) {
  const [form, setForm] = useState<AddNvrFormValues>(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setShowPassword(false);
  }, [open]);

  const reset = useCallback(() => {
    setForm(emptyForm());
    setShowPassword(false);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    reset();
    onClose();
  }, [submitting, onClose, reset]);

  const handleCheckConnection = useCallback(() => {
    const host = form.host.trim();
    if (!host) {
      console.warn("[AddNvrDialog] Check connection: host is empty");
      return;
    }
    const scheme = form.useHttps ? "https" : "http";
    const portPart = form.port.trim() ? `:${form.port.trim()}` : "";
    console.info("[AddNvrDialog] Check connection", `${scheme}://${host}${portPart}`);
  }, [form.host, form.port, form.useHttps]);

  const handleOk = useCallback(async () => {
    const name = form.name.trim();
    if (!name || !form.brand || !form.host.trim()) {
      console.warn("[AddNvrDialog] Missing required fields");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form);
      reset();
      onClose();
    } catch (e) {
      console.error("[AddNvrDialog] Submit failed", e);
    } finally {
      setSubmitting(false);
    }
  }, [form, onClose, onSubmit, reset]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="add-nvr-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: "1px solid #E0E0E0",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        id="add-nvr-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 2.5,
          bgcolor: "#F5F7FA",
          borderBottom: "1px solid #E5E7EB",
          fontFamily: "Roboto, sans-serif",
          fontWeight: 600,
          fontSize: 18,
          color: "#212121",
        }}
      >
        Add NVR
        <IconButton aria-label="Close" onClick={handleClose} size="small" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3, pb: 1 }}>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          onSubmit={(e) => {
            e.preventDefault();
            void handleOk();
          }}
        >
          <TextField
            required
            label="NVR Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            size="small"
            InputLabelProps={{ required: false }}
            sx={{ "& .MuiInputLabel-root": { "& .MuiFormLabel-asterisk": { color: "#D32F2F" } } }}
          />

          <FormControl fullWidth size="small" required>
            <InputLabel id="nvr-brand-label">NVR Brand</InputLabel>
            <Select
              labelId="nvr-brand-label"
              label="NVR Brand"
              value={form.brand}
              onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
            >
              {DEFAULT_BRANDS.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography
              component="label"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: "#212121",
                display: "block",
                mb: 1,
              }}
            >
              IP / Domain Name{" "}
              <Box component="span" sx={{ color: "#D32F2F" }}>
                *
              </Box>
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.useHttps}
                    onChange={(e) => setForm((f) => ({ ...f, useHttps: e.target.checked }))}
                    size="small"
                  />
                }
                label="HTTPS"
                sx={{ mr: 1 }}
              />
              <TextField
                required
                placeholder="192.168.x.x or host"
                value={form.host}
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                size="small"
                sx={{ flex: "1 1 200px", minWidth: 160 }}
                InputLabelProps={{ shrink: false }}
                label=""
                hiddenLabel
              />
              <TextField
                type="number"
                placeholder="Port"
                value={form.port}
                onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
                size="small"
                sx={{ width: 100 }}
                inputProps={{ min: 1, max: 65535 }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: "#6B7280", fontFamily: "Roboto, sans-serif" }}>
              For example, IP Address xxx.xxx.xxx.xxx / Domain Name: xxx.xxx.com
            </Typography>
          </Box>

          <TextField
            required
            label="User Name"
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            fullWidth
            size="small"
            autoComplete="username"
            InputLabelProps={{ required: false }}
          />

          <TextField
            required
            label="Password"
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            fullWidth
            size="small"
            autoComplete="new-password"
            InputLabelProps={{ required: false }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCheckConnection}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                color: "#4A5565",
                borderColor: "#E5E7EB",
                bgcolor: "#FAFAFA",
              }}
            >
              Check Connection
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB", gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: "none", color: "#4A5565" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleOk()}
          disabled={submitting}
          sx={{
            textTransform: "none",
            bgcolor: "#42A5F5",
            "&:hover": { bgcolor: "#1E88E5" },
            minWidth: 88,
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
